import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { api } from '@carrent/api'

function StatBox({ val, label, color, onClick }) {
  return (
    <div className="stat-box" style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div className="stat-box__val" style={color ? { color } : {}}>{val ?? '—'}</div>
      <div className="stat-box__label">{label}</div>
    </div>
  )
}

// Компактный виджет напоминаний для дашборда
function RemindersWidget({ data, onViewAll }) {
  if (!data) return null

  const { items, counts } = data
  const hasErrors = counts.error > 0
  const hasWarnings = counts.warning > 0
  const total = counts.total

  if (total === 0) {
    return (
      <div className="card" style={{
        display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
        background: 'rgba(34,197,94,0.05)', borderColor: 'rgba(34,197,94,0.3)',
      }}>
        <span style={{ fontSize: '1.5rem' }}>✅</span>
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--success)' }}>
            Всё в порядке
          </p>
          <p className="text-muted" style={{ fontSize: '0.78rem' }}>
            Нет просроченных или скоро истекающих документов
          </p>
        </div>
      </div>
    )
  }

  const accentColor = hasErrors ? 'var(--danger)' : 'var(--warning)'
  const borderColor = hasErrors ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'
  const bg          = hasErrors ? 'rgba(239,68,68,0.05)' : 'rgba(245,158,11,0.05)'

  return (
    <div className="card" style={{ background: bg, borderColor, padding: 0, overflow: 'hidden' }}>
      {/* Шапка виджета */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: 'var(--gap-sm) var(--gap)',
        borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.1rem' }}>{hasErrors ? '🔴' : '🟡'}</span>
          <p style={{ fontWeight: 700, fontSize: '0.9rem', color: accentColor }}>
            {hasErrors ? `${counts.error} просрочено` : ''}
            {hasErrors && hasWarnings ? ' · ' : ''}
            {hasWarnings ? `${counts.warning} скоро истекает` : ''}
          </p>
        </div>
        <button onClick={onViewAll}
          style={{ background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
          Все →
        </button>
      </div>

      {/* Первые 3 машины */}
      <div style={{ padding: 'var(--gap-sm) var(--gap)',
        display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.slice(0, 3).map(car => {
          const topReminder = car.reminders[0]
          const s = {
            error:   { color: 'var(--danger)',  bg: 'rgba(239,68,68,0.08)' },
            warning: { color: 'var(--warning)', bg: 'rgba(245,158,11,0.08)' },
            info:    { color: 'var(--accent)',  bg: 'rgba(59,130,246,0.08)' },
          }[car.top_level] || {}

          return (
            <div key={car.car_id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 10px', background: s.bg,
              borderRadius: 'var(--r-sm)', gap: 'var(--gap-sm)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '0.85rem', fontWeight: 600,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {car.car_brand} {car.car_model}
                  <span style={{
                    marginLeft: 6, fontFamily: 'monospace', fontSize: '0.72rem',
                    background: '#fff', color: '#000', padding: '0 5px',
                    borderRadius: 3, border: '1px solid #555',
                  }}>{car.car_plate}</span>
                </p>
                <p style={{ fontSize: '0.72rem', color: s.color, marginTop: 2 }}>
                  {topReminder.icon} {topReminder.label}: {topReminder.text}
                </p>
              </div>
              {car.reminders.length > 1 && (
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                  +{car.reminders.length - 1}
                </span>
              )}
            </div>
          )
        })}

        {items.length > 3 && (
          <button onClick={onViewAll} style={{
            background: 'none', border: `1px dashed ${borderColor}`,
            borderRadius: 'var(--r-sm)', padding: '6px',
            cursor: 'pointer', color: accentColor, fontSize: '0.78rem',
            width: '100%', textAlign: 'center',
          }}>
            Ещё {items.length - 3} машин с напоминаниями →
          </button>
        )}
      </div>
    </div>
  )
}

export default function AdminDashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats]         = useState(null)
  const [reminders, setReminders] = useState(null)
  const [loading, setLoading]     = useState(true)

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats').catch(() => ({ data: { data: {} } })),
      api.get('/admin/reminders', { params: { days: 30 } }).catch(() => ({ data: { data: { items: [], counts: {} } } })),
    ]).then(([s, r]) => {
      setStats(s.data?.data || {})
      setReminders(r.data?.data || { items: [], counts: {} })
    }).finally(() => setLoading(false))
  }, [])

  const firstName = user?.full_name?.split(' ')[0]
  const totalReminders = reminders?.counts?.total || 0
  const errorCount = reminders?.counts?.error || 0

  return (
    <div className="p-page flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap)' }}>
      {/* Приветствие */}
      <div>
        <p className="text-muted" style={{ fontSize: '0.85rem' }}>Панель администратора</p>
        <h1 style={{ fontSize: '1.4rem', marginTop: 2 }}>Привет, {firstName} 👋</h1>
      </div>

      {loading
        ? <div className="spinner" style={{ alignSelf: 'center' }} />
        : <>
          {/* Сводка */}
          <p className="section-title">Обзор</p>
          <div className="stat-grid">
            <StatBox val={stats?.pending_users}  label="На проверке"
              color={stats?.pending_users > 0 ? 'var(--warning)' : undefined}
              onClick={() => navigate('/admin/users?status=pending')} />
            <StatBox val={stats?.active_trips}   label="Активных поездок"
              color={stats?.active_trips > 0 ? 'var(--accent)' : undefined}
              onClick={() => navigate('/admin/trips?status=active')} />
            <StatBox val={stats?.available_cars} label="Доступно машин"
              color="var(--success)"
              onClick={() => navigate('/admin/cars?status=available')} />
            <StatBox val={stats?.pending_trips}  label="Ждут подтв."
              color={stats?.pending_trips > 0 ? 'var(--warning)' : undefined}
              onClick={() => navigate('/admin/trips?status=finished_by_user')} />
          </div>

          {/* Быстрые действия */}
          <p className="section-title">Быстрые действия</p>
          {[
            {
              label: '🌍 Регионы',
              badge: null,
              badgeColor: 'var(--accent)',
              to: '/admin/regions',
            },
            {
              label: '📊 Аналитика и отчёты',
              badge: null,
              badgeColor: 'var(--accent)',
              to: '/admin/analytics',
            },
            {
              label: '👥 Заявки водителей',
              badge: stats?.pending_users,
              badgeColor: 'var(--warning)',
              to: '/admin/users?status=pending',
            },
            {
              label: '🚗 Управление машинами',
              badge: stats?.in_use_cars ? `${stats.in_use_cars} в поездке` : null,
              badgeColor: 'var(--accent)',
              to: '/admin/cars',
            },
            {
              label: '📋 Поездки',
              badge: stats?.pending_trips,
              badgeColor: 'var(--warning)',
              to: '/admin/trips',
            },
            {
              label: '💬 Обращения водителей',
              badge: stats?.active_tickets,
              badgeColor: stats?.urgent_tickets > 0 ? 'var(--danger)' : 'var(--accent)',
              to: '/admin/tickets',
            },
            {
              label: '🔔 Напоминания',
              badge: totalReminders > 0 ? totalReminders : null,
              badgeColor: errorCount > 0 ? 'var(--danger)' : 'var(--warning)',
              to: '/admin/reminders',
            },
          ].map(({ label, badge, badgeColor, to }) => (
            <div key={to} className="card list-item"
              style={{ justifyContent: 'space-between', cursor: 'pointer', padding: 'var(--gap)' }}
              onClick={() => navigate(to)}>
              <span style={{ fontSize: '0.95rem' }}>{label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {badge !== null && badge !== undefined && badge !== 0 && (
                  <span style={{
                    background: badgeColor, color: '#fff',
                    fontSize: '0.75rem', fontWeight: 700,
                    padding: '2px 8px', borderRadius: 99,
                    opacity: 0.9,
                  }}>{badge}</span>
                )}
                <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>›</span>
              </div>
            </div>
          ))}

          {/* Виджет напоминаний */}
          {totalReminders > 0 && (
            <>
              <p className="section-title"
                style={{ color: errorCount > 0 ? 'var(--danger)' : 'var(--warning)' }}>
                {errorCount > 0 ? '🔴 Требует внимания' : '🟡 Напоминания'}
              </p>
              <RemindersWidget data={reminders}
                onViewAll={() => navigate('/admin/reminders')} />
            </>
          )}
        </>
      }
    </div>
  )
}
